import * as React from 'react';
import SearchField from './SearchField';
import { useEffect, useRef, useState } from 'react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAssets from '@/src/Processing/components/hooks/useAssets';

/* ---- minimal asset picker (replaces the classic Library modal) ---- */

interface AssetPickerProps {
    onSelect: (url: string) => void;
    onClose: () => void;
}

export default function AssetPicker({ onSelect, onClose }: AssetPickerProps) {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const { uploadAsset } = useAssets();
    const [assets, setAssets] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let mounted = true;
        authenticatedApi
            .get(`/${orgId}/assets`)
            .then((r) => {
                if (mounted)
                    setAssets(
                        (r.data ?? []).sort(
                            (a: any, b: any) =>
                                new Date(b.createdAt).getTime() -
                                new Date(a.createdAt).getTime(),
                        ),
                    );
            })
            .catch(() => {})
            .finally(() => mounted && setIsLoading(false));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const upload = async (file: File) => {
        setIsUploading(true);
        try {
            const result = await uploadAsset({
                // @ts-ignore — FormData accepts File
                asset: file,
                name: file.name,
                folder: 'customizer',
                orgId: String(orgId),
            });
            if (result?.url) onSelect(result.url);
        } finally {
            setIsUploading(false);
        }
    };

    const filtered = assets.filter((a) =>
        (a.name ?? '').toLowerCase().includes(search.toLowerCase()),
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
            onClick={onClose}
        >
            <div
                className="flex max-h-[70vh] w-full max-w-[560px] flex-col rounded-[15px] border border-border bg-background p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-3 flex items-center justify-between">
                    <span className="ps-label">Choose asset</span>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={isUploading}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-3.5 py-1.5 font-[inherit] text-[12px] font-semibold text-background"
                        >
                            {isUploading ? 'Uploading…' : 'Upload'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            Close
                        </button>
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*,.cube"
                        className="hidden"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) upload(f);
                        }}
                    />
                </div>
                <SearchField
                    className="mb-2 w-full"
                    placeholder="Search assets…"
                    value={search}
                    onChange={setSearch}
                />
                <div className="mb-3 flex items-center gap-2">
                    <input
                        className="ps-input flex-1 font-mono text-[11.5px]"
                        placeholder="…or paste a URL"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && url.trim())
                                onSelect(url.trim());
                        }}
                    />
                    {url.trim() && (
                        <button
                            type="button"
                            onClick={() => onSelect(url.trim())}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                        >
                            Use
                        </button>
                    )}
                </div>
                <div className="grid flex-1 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-5">
                    {isLoading &&
                        Array.from({ length: 10 }).map((_, i) => (
                            <div
                                key={i}
                                className="aspect-square animate-pulse rounded-[9px] bg-secondary"
                            />
                        ))}
                    {!isLoading && filtered.length === 0 && (
                        <p className="col-span-full py-4 text-[12.5px] text-muted-foreground">
                            No assets{search ? ' match' : ' yet — upload one'}.
                        </p>
                    )}
                    {filtered.map((a) => (
                        <button
                            key={a.id}
                            type="button"
                            title={a.name}
                            onClick={() => onSelect(a.url)}
                            className="cursor-pointer border-0 bg-transparent p-0 text-left"
                        >
                            <span className="block aspect-square overflow-hidden rounded-[9px] bg-secondary">
                                <img
                                    src={a.url}
                                    alt={a.name}
                                    loading="lazy"
                                    className="h-full w-full object-contain"
                                />
                            </span>
                            <span className="mt-1 block overflow-hidden text-ellipsis whitespace-nowrap text-[10px] text-faint">
                                {a.name}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
